import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { signOut } from '../slices/authApiSlice';
import { useUserSignOutMutation } from '../slices/usersApiSlice';
import { FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import logo from '../assets/images/jdm-logo-270x270.jpeg';
import { useTheme } from '../hooks/useTheme';
import { usePalette } from '../hooks/usePalette';
import { THEME_LIST } from '../theme/themes';
import { PALETTE_LIST, DEFAULT_LIGHT_PALETTE } from '../theme/palettes';


export default function Header() {
  const { userInfo } = useSelector((state) => state.auth);
  const { pathname } = useLocation();
  const { themeName, setTheme, linkedPaletteId } = useTheme();
  const { effectivePaletteId, setPaletteById } = usePalette();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // The user's own pages all live under /me/ (e.g. /me/drives/drive/:id) and
  // belong to the user menu, not the public Benchmarks/Drives tabs.
  // /me/blogs keeps highlighting the Blog tab, as before.
  const isMePage = pathname === '/me' || pathname.startsWith('/me/');
  const isBenchmarksActive = !isMePage && (pathname.startsWith('/all_benchmarks') || pathname.includes('/benchmark/'));
  const isDrivesActive = !isMePage && (pathname.startsWith('/all_drives') || pathname.includes('/drive/'));
  const isBlogActive = pathname.startsWith('/blog') || pathname.startsWith('/me/blogs');
  const isDownloadActive = pathname.startsWith('/download_jdiskmark_desktop_app');
  const isProfileActive = Boolean(userInfo) && !isBenchmarksActive && !isDrivesActive && !isBlogActive && !isDownloadActive &&
    (isMePage || pathname.startsWith('/admin/'));

  const [signOutApiCall] = useUserSignOutMutation();

  const signOutHandler = async () => {
    try {
      await signOutApiCall().unwrap();
      dispatch(signOut());
      toast.success("Success: You have signed out");
      navigate("/sign_in");
    } catch (err) {
      toast.error(err?.data?.message || err.error);
      console.log(err);
    }
  }


  return (
    <header>
      <Navbar collapseOnSelect expand='xl'>
        <Container fluid className='nav_container'>
          <Navbar.Brand
            as={Link}
            to="/"
            className='d-flex align-items-center'
          >
            <img src={logo} alt="JDiskMark logo" className='header__logo me-2'/>
            <div>
              <h2 className='header__text py-0 mx-2 my-0'>JDiskMark</h2>
              <h4 className='header__text py-0 mx-2 my-0'>{">> >> >> >>"}</h4>
              <h4 className='header__text py-0 mx-2 my-0'>Benchmarking</h4>
            </div>
            <ul className='me-3 mt-2 mb-0'>
              <li className='header__text'>Test</li>
              <li className='header__text'>Know</li>
              <li className='header__text'>Choose</li>
            </ul>
            <pre className='header__text-art'>
              {String.raw`
\\ \\
// //
      `}
            </pre>
            <h3 className='header__text mb-0' >Better</h3>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls='basic-navbar-nav'></Navbar.Toggle>
          <Navbar.Collapse id='basic-navbar-nav'>
            <Nav>
              <LinkContainer to="/all_benchmarks">
                <Nav.Link className='nav-link' active={isBenchmarksActive}>Benchmarks</Nav.Link>
              </LinkContainer>
              <LinkContainer to="/all_drives">
                <Nav.Link className='nav-link' active={isDrivesActive}>Drives</Nav.Link>
              </LinkContainer>
              <LinkContainer to="/blog">
                <Nav.Link className='nav-link' active={isBlogActive}>Blog</Nav.Link>
              </LinkContainer>
              <LinkContainer to="/download_jdiskmark_desktop_app">
                <Nav.Link className='nav-link' active={isDownloadActive}>Download</Nav.Link>
              </LinkContainer>
              <div className='theme_switch_userId_group'>
                {userInfo ? (
                  <NavDropdown
                    className={`dropdown-justify${isProfileActive ? ' nav-dropdown--profile-active' : ''}`}
                    title={
                      <span className={`nav-email${isProfileActive ? ' nav-email--active' : ''}`}>
                        <FaUser className='me-2' />
                        {userInfo.firstName}
                      </span>}
                    id='user-menu'
                    align='end'
                    autoClose='outside'
                  >

                    <NavDropdown.Item onClick={() => navigate(`/me/profile`)}>
                      My Profile
                    </NavDropdown.Item>

                    <NavDropdown.Item onClick={() => navigate(`/me/benchmarks`)}>
                      My Benchmarks
                    </NavDropdown.Item>

                    <NavDropdown.Item onClick={() => navigate(`/me/drives`)}>
                      My Drives
                    </NavDropdown.Item>

                    <NavDropdown.Item onClick={() => navigate(`/me/systems`)}>
                      My Systems
                    </NavDropdown.Item>

                    <NavDropdown.Item onClick={() => navigate(`/me/blogs`)}>
                      My Blog Posts
                    </NavDropdown.Item>

                    <NavDropdown.Divider />

                    <div className='dropdown-submenu'>
                      <button type='button' className='dropdown-submenu__trigger dropdown-item d-flex justify-content-between align-items-center' aria-haspopup='menu'>
                        <span>Theme</span>
                        <span className='dropdown-submenu__arrow'>&lsaquo;</span>
                      </button>
                      <div className='dropdown-submenu__menu dropdown-menu'>
                        {THEME_LIST.map((t) => (
                          <NavDropdown.Item
                            key={t.id}
                            active={themeName === t.id}
                            onClick={() => setTheme(t.id)}
                            className={themeName === t.id ? 'dropdown-item--active' : ''}
                          >
                            {themeName === t.id ? '✓ ' : '\u2003'}{t.displayName}
                          </NavDropdown.Item>
                        ))}
                      </div>
                    </div>

                    {!linkedPaletteId && (
                      <div className='dropdown-submenu'>
                        <button type='button' className='dropdown-submenu__trigger dropdown-item d-flex justify-content-between align-items-center' aria-haspopup='menu'>
                          <span>Graph Palette</span>
                          <span className='dropdown-submenu__arrow'>&lsaquo;</span>
                        </button>
                        <div className='dropdown-submenu__menu dropdown-menu'>
                          {PALETTE_LIST.map((p) => (
                            <NavDropdown.Item
                              key={p.id}
                              active={effectivePaletteId === p.id}
                              onClick={() => setPaletteById(p.id)}
                              className={effectivePaletteId === p.id ? 'dropdown-item--active' : ''}
                            >
                              {effectivePaletteId === p.id ? '✓ ' : '\u2003'}{p.displayName}{p.id === DEFAULT_LIGHT_PALETTE ? ' (Default)' : ''}
                            </NavDropdown.Item>
                          ))}
                        </div>
                      </div>
                    )}

                    {userInfo.isAdmin && (
                      <>
                        <NavDropdown.Divider />

                        <NavDropdown.Item onClick={() => navigate(`/admin/home`)}>
                          Admin Home
                        </NavDropdown.Item>

                        {userInfo.primaryEmail === process.env.REACT_APP_CHIEF_ADMIN_EMAIL && (
                          <NavDropdown.Item onClick={() => navigate(`/admin/role_management`)}>
                            Role Management
                          </NavDropdown.Item>
                        )}
                      </>
                    )}

                    <NavDropdown.Divider />

                    <NavDropdown.Item onClick={signOutHandler}>
                      Sign Out
                    </NavDropdown.Item>
                  </NavDropdown>

                ) : (
                  <LinkContainer to="/sign_in">
                    <Nav.Link className='nav-link__sign-in'>
                      <FaUser /> Sign In
                    </Nav.Link>
                  </LinkContainer>
                )}
              </div>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </header>
  );
}
